import { useState, useEffect, useRef } from 'react'
import { Plus, Edit2, Trash2, FileText, StickyNote, X, Save, FolderOpen, Upload, Download, Image as ImageIcon, ClipboardList, ArrowUp, ArrowDown, FlaskConical, Megaphone, Link as LinkIcon, Lock, Unlock } from 'lucide-react'
import ReactQuill from 'react-quill-new'
import 'react-quill-new/dist/quill.snow.css'
import toast from 'react-hot-toast'
import api from '../../services/api'

// Les variables dynamiques
const DYNAMIC_VARS =[
    { label: 'Prénom', val: '{{prenom}}' },
    { label: 'Nom', val: '{{nom}}' },
    { label: 'Date', val: '{{date_jour}}' },
    { label: 'Civilité', val: '{{civilite}}' },
    { label: 'Confrère (Nom)', val: '{{confrere_nom}}' },
    { label: 'Confrère (Spé)', val: '{{confrere_specialite}}' },
    { label: 'Fichier Dynamique 📎', val: '{{fichier_joint}}' }
]

const sanitizeFieldName = (label) => {
    if (!label) return 'champ_inconnu';
    return label.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[^a-z0-9]/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '');
}


export default function LibraryPage() {
    const [activeTab, setActiveTab] = useState('notes')
    const [items, setItems] = useState([])
    const [libraryFiles, setLibraryFiles] = useState([]) // Pour lister les fichiers à joindre
    const [loading, setLoading] = useState(false)
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [editingItem, setEditingItem] = useState(null)
    const [formData, setFormData] = useState({ name: '', type: '', content: '', linked_form_id: '' }) // linked_form_id pour les courriers liés à un formulaire
    const [isUploadModalOpen, setIsUploadModalOpen] = useState(false)
    const [uploadData, setUploadData] = useState({ name: '', category: 'guide', file: null, is_public: false })
    const [isFormBuilderOpen, setIsFormBuilderOpen] = useState(false)
    const [editingForm, setEditingForm] = useState(null)
    const [formBuilderData, setFormBuilderData] = useState({ name: '', description: '', fields:[], is_internal: false })
    const [biologyGroups, setBiologyGroups] = useState([])
    const [formTemplates, setFormTemplates] = useState([])

    // Les références pour les éditeurs
    const quillRef = useRef(null)
    const textAreaRef = useRef(null)

    // NOUVEAU : La fonction d'insertion au curseur
    const insertVariable = (tag) => {
        if (activeTab === 'notes' || formData.type === 'campaign_sms') {
            // Mode Textarea (Notes et SMS)
            const textarea = textAreaRef.current;
            if (textarea) {
                const start = textarea.selectionStart;
                const end = textarea.selectionEnd;
                const newContent = formData.content.substring(0, start) + tag + formData.content.substring(end);
                setFormData(prev => ({...prev, content: newContent}));
                // Remet le curseur après le mot inséré
                setTimeout(() => {
                    textarea.focus();
                    textarea.setSelectionRange(start + tag.length, start + tag.length);
                }, 0);
            } else {
                setFormData(prev => ({...prev, content: prev.content + ' ' + tag}));
            }
        } else {
            // Mode ReactQuill (Courriers et Emails)
            if (!quillRef.current) return;
            const editor = quillRef.current.getEditor();
            const cursorPosition = editor.getSelection()?.index;
            
            if (cursorPosition !== undefined) {
                editor.insertText(cursorPosition, tag);
                editor.setSelection(cursorPosition + tag.length);
            } else {
                const length = editor.getLength();
                editor.insertText(length, ' ' + tag);
            }
        }
    };



    const quillModules = {
        toolbar: [
            [{ 'header':[1, 2, false] }],['bold', 'italic', 'underline', 'strike'],[{ 'list': 'ordered'}, { 'list': 'bullet' }],['clean']
        ],
    }

    const fetchItems = async () => {
        setLoading(true)
        try {
            // On charge TOUJOURS les fichiers ET les formulaires pour les menus déroulants
            api.get('/api/library/files').then(res => setLibraryFiles(res.data)).catch(()=>{})
            api.get('/api/forms/templates').then(res => setFormTemplates(res.data)).catch(()=>{})

            let endpoint = ''

            // NOTE : Les campagnes sont stockées dans les 'documents' avec des catégories spécifiques
            if (activeTab === 'notes') endpoint = '/api/library/notes'
            else if (activeTab === 'documents' || activeTab === 'campaigns') endpoint = '/api/library/documents'
            else if (activeTab === 'files') endpoint = '/api/library/files'
            else if (activeTab === 'forms') endpoint = '/api/forms/templates'
            
            if (activeTab === 'biology') {
                const res = await api.get('/api/settings/')
                const setting = res.data.find(s => s.setting_key === 'biological_analyses_config')
                if (setting && setting.setting_value) {
                    setBiologyGroups(JSON.parse(setting.setting_value))
                } else {
                    setBiologyGroups([
                        { title: "Bilan Glucidique", items:["Glycémie à jeun", "Hémoglobine glyquée (HbA1c)", "Insulinémie à jeun"] },
                        { title: "Bilan Lipidique (EAL)", items:["Cholestérol Total", "HDL-Cholestérol", "LDL-Cholestérol", "Triglycérides"] },
                        { title: "Bilan Thyroïdien", items:["TSH US", "T3 libre (T3L)", "T4 libre (T4L)"] },
                        { title: "Statut Martial & Vitamines", items:["Ferritine", "Fer sérique", "Vitamine D (25-OH D3)", "Vitamine B12"] }
                    ])
                }
            } else {
                const res = await api.get(endpoint)
                setItems(res.data)
            }
        } catch (error) { toast.error("Erreur chargement") } 
        finally { setLoading(false) }
    }

    useEffect(() => { fetchItems() }, [activeTab])

    // --- LOGIQUE TEXTES (Notes/Docs) ---
    const handleSaveText = async () => {
        try {
            const endpoint = activeTab === 'notes' ? '/api/library/notes' : '/api/library/documents'
            const payload = { name: formData.name, content: formData.content,[activeTab === 'notes' ? 'note_type' : 'category']: formData.type }
            if (activeTab === 'documents') payload.linked_form_id = formData.linked_form_id ? parseInt(formData.linked_form_id) : null;

            if (editingItem) await api.patch(`${endpoint}/${editingItem.id}`, payload)
            else await api.post(endpoint, payload)
            toast.success("Enregistré"); setIsModalOpen(false); fetchItems()
        } catch (error) { toast.error("Erreur sauvegarde") }
    }

    const handleOpenTextModal = (item = null) => {
        setEditingItem(item)
        if (item) {
            setFormData({ name: item.name, type: activeTab === 'notes' ? item.note_type : item.category, content: item.content, linked_form_id: item.linked_form_id || '' })
        } else {
            setFormData({ name: '', type: activeTab === 'notes' ? 'follow_up' : (activeTab === 'campaigns' ? 'campaign_sms' : 'courrier'), content: '', linked_form_id: '' })
        }
        setIsModalOpen(true)
    }

        // --- LOGIQUE FICHIERS ---
    const handleUploadFile = async () => {
        if (!uploadData.file || !uploadData.name) return toast.error("Fichier et nom requis")
        const formDataUpload = new FormData()
        formDataUpload.append('file', uploadData.file); formDataUpload.append('name', uploadData.name); formDataUpload.append('category', uploadData.category); formDataUpload.append('is_public', uploadData.is_public)
        try {
            await api.post('/api/library/files/upload', formDataUpload, { headers: { 'Content-Type': 'multipart/form-data' } })
            toast.success("Fichier ajouté"); setIsUploadModalOpen(false); setUploadData({ name: '', category: 'guide', file: null, is_public: false }); fetchItems()
        } catch (e) { toast.error("Erreur upload") }
    }

    const handleTogglePublic = async (fileId) => {
        try {
            await api.patch(`/api/library/files/${fileId}/toggle-public`)
            fetchItems()
            toast.success("Droits d'accès modifiés")
        } catch (e) { toast.error("Erreur") }
    }


    const handleDownload = async (file) => {
        try {
            const response = await api.get(`/api/library/files/${file.id}/download`, { responseType: 'blob' })
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a'); link.href = url; link.setAttribute('download', `${file.name}.${file.file_type}`); document.body.appendChild(link); link.click(); link.parentNode.removeChild(link);
        } catch (error) { toast.error("Erreur téléchargement") }
    }

    // --- LOGIQUE FORMULAIRES DYNAMIQUES (BUILDER) ---
    const handleOpenFormBuilder = (item = null) => {
        setEditingForm(item)
        if (item) {
            let parsedFields =[]
            try { parsedFields = JSON.parse(item.fields_json) } catch(e){}
            setFormBuilderData({ name: item.name, description: item.description || '', fields: parsedFields })
        } else {
            setFormBuilderData({ name: '', description: '', fields:[] })
        }
        setIsFormBuilderOpen(true)
    }

    const handleAddFormField = () => {
        setFormBuilderData(prev => ({
            ...prev,
            fields:[...prev.fields, { id: `q_${Date.now()}`, type: 'text', label: '', required: false, options: '' }]
        }))
    }

    const handleUpdateFormField = (id, key, value) => {
        setFormBuilderData(prev => ({
            ...prev,
            fields: prev.fields.map(f => f.id === id ? { ...f, [key]: value } : f)
        }))
    }

    const handleRemoveFormField = (id) => {
        setFormBuilderData(prev => ({
            ...prev,
            fields: prev.fields.filter(f => f.id !== id)
        }))
    }

    const handleMoveFormField = (index, direction) => {
        const newFields = [...formBuilderData.fields];
        if (direction === 'up' && index > 0) {
            [newFields[index - 1], newFields[index]] = [newFields[index], newFields[index - 1]];
        } else if (direction === 'down' && index < newFields.length - 1) {
            [newFields[index + 1], newFields[index]] = [newFields[index], newFields[index + 1]];
        }
        setFormBuilderData(prev => ({ ...prev, fields: newFields }));
    }

    const handleSaveFormTemplate = async () => {
        if (!formBuilderData.name) return toast.error("Le questionnaire doit avoir un nom.")
        if (formBuilderData.fields.length === 0) return toast.error("Ajoutez au moins une question.")
        
        // 🛡️ SÉCURITÉ : On vérifie que deux questions ne génèrent pas la même variable
        const labels = formBuilderData.fields.map(f => sanitizeFieldName(f.label));
        const uniqueLabels = new Set(labels);
        if (labels.length !== uniqueLabels.size) {
            return toast.error("Deux questions ne peuvent pas avoir exactement le même intitulé. Modifiez vos textes.");
        }
        
        try {
            const payload = {

                name: formBuilderData.name,
                description: formBuilderData.description,
                fields_json: JSON.stringify(formBuilderData.fields),
                is_internal: formBuilderData.is_internal
            }

            if (editingForm) {
                await api.patch(`/api/forms/templates/${editingForm.id}`, payload)
                toast.success("Questionnaire mis à jour")
            } else {
                await api.post('/api/forms/templates', payload)
                toast.success("Questionnaire créé")
            }
            setIsFormBuilderOpen(false)
            fetchItems()
        } catch (error) { toast.error("Erreur de sauvegarde") }
    }
    // ==========================================================

    // --- SUPPRESSION GLOBALE ---
    const handleDelete = async (id) => {
        if (!confirm("Supprimer définitivement cet élément ?")) return
        try {
            let endpoint = ''
            if (activeTab === 'notes') endpoint = `/api/library/notes/${id}`
            else if (activeTab === 'documents' || activeTab === 'campaigns') endpoint = `/api/library/documents/${id}`
            else if (activeTab === 'files') endpoint = `/api/library/files/${id}`
            else if (activeTab === 'forms') endpoint = `/api/forms/templates/${id}`
            await api.delete(endpoint)
            toast.success("Supprimé"); fetchItems()
        } catch (error) { toast.error("Erreur suppression") }
    }

    // --- LOGIQUE DE LA VUE BIOLOGIE ---
    const handleSaveBiology = async () => {
        try {
            const cleanGroups = biologyGroups.map(g => ({ title: g.title, items: g.items }))
            await api.patch('/api/settings/biological_analyses_config', { value: JSON.stringify(cleanGroups) })
            toast.success("Bilans mis à jour")
        } catch(e) { toast.error("Erreur sauvegarde") }
    }
    const addBioGroup = () => setBiologyGroups([...biologyGroups, { title: 'Nouveau Bilan', items: [] }])
    const updateBioGroupTitle = (idx, val) => { const n = [...biologyGroups]; n[idx].title = val; setBiologyGroups(n) }
    const updateBioGroupItems = (idx, val) => { const n = [...biologyGroups]; n[idx].raw_items = val; n[idx].items = val.split(',').map(s=>s.trim()).filter(Boolean); setBiologyGroups(n) }
    const removeBioGroup = (idx) => { if(confirm("Supprimer ce groupe ?")) setBiologyGroups(biologyGroups.filter((_, i) => i !== idx)) }
    const moveBioGroup = (idx, dir) => {
        const n = [...biologyGroups];
        if (dir === 'up' && idx > 0) [n[idx-1], n[idx]] = [n[idx], n[idx-1]];
        if (dir === 'down' && idx < n.length - 1) [n[idx+1], n[idx]] =[n[idx], n[idx+1]];
        setBiologyGroups(n)
    }

    // --- FILTRAGE DE L'AFFICHAGE SELON L'ONGLET ---
    let displayItems = items.map(i => ({...i, _type: 'item'}));
    if (activeTab === 'campaigns') {
        displayItems = items.filter(i => i.category === 'campaign_sms' || i.category === 'campaign_email').map(i => ({...i, _type: 'item'}));
    } else if (activeTab === 'documents') {
        displayItems = items.filter(i => i.category !== 'campaign_sms' && i.category !== 'campaign_email').map(i => ({...i, _type: 'item'}));
    } else if (activeTab === 'notes') {
        // On mélange les Trames Textes et les Masques (Formulaires internes)
        const textNotes = items.map(i => ({...i, _type: 'item'}));
        const internalForms = formTemplates.filter(f => f.is_internal).map(f => ({...f, _type: 'form'}));
        displayItems = [...textNotes, ...internalForms];
    } else if (activeTab === 'forms') {
        // On ne montre que les questionnaires externes
        displayItems = formTemplates.filter(f => !f.is_internal).map(f => ({...f, _type: 'form'}));
    } else if (activeTab === 'files') {
        displayItems = items.map(i => ({...i, _type: 'item'}));
    }
    
    // Sécurisation de la suppression hybride
    const handleDeleteItem = async (item) => {
        if (!confirm("Supprimer définitivement cet élément ?")) return;
        try {
            let endpoint = '';
            if (item._type === 'form') endpoint = `/api/forms/templates/${item.id}`;
            else if (activeTab === 'notes') endpoint = `/api/library/notes/${item.id}`;
            else if (activeTab === 'documents' || activeTab === 'campaigns') endpoint = `/api/library/documents/${item.id}`;
            else if (activeTab === 'files') endpoint = `/api/library/files/${item.id}`;
            
            await api.delete(endpoint);
            toast.success("Supprimé"); 
            fetchItems();
        } catch (error) { toast.error("Erreur suppression"); }
    }

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-end">
                <div>
                    <h1 className="text-2xl font-serif text-gray-800">Bibliothèque</h1>
                    <p className="text-gray-500 mt-1">Gérez vos modèles et ressources.</p>
                </div>
            </div>

            <div className="flex gap-4 border-b border-gray-200 overflow-x-auto scrollbar-hide">
                {[
                    { id: 'notes', label: 'Trames & Masques', icon: StickyNote },
                    { id: 'documents', label: 'Documents', icon: FileText },
                    { id: 'campaigns', label: 'Campagnes & Messages', icon: Megaphone }, 
                    { id: 'forms', label: 'Questionnaires Patients', icon: ClipboardList },
                    { id: 'files', label: 'Fichiers', icon: FolderOpen },
                    { id: 'biology', label: 'Bilans Biologiques', icon: FlaskConical }
                ].map(tab => (
                    <button 
                        key={tab.id} onClick={() => setActiveTab(tab.id)}
                        className={`pb-3 px-4 flex items-center gap-2 font-medium transition-all border-b-2 whitespace-nowrap ${activeTab === tab.id ? 'border-sage-500 text-sage-700' : 'border-transparent text-gray-400 hover:text-gray-600'}`}
                    >
                        <tab.icon className="w-4 h-4" /> {tab.label}
                    </button>
                ))}
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 min-h-[400px]">
                
                {/* --- VUE CLASSIQUE --- */}
                {activeTab !== 'biology' && (
                    <>
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="font-bold text-gray-700">
                                {activeTab === 'campaigns' ? 'Modèles de Campagnes Marketing' : 'Contenu'}
                            </h2>
                            <div className="flex gap-2">
                                {activeTab === 'notes' ? (
                                    <>
                                        <button onClick={() => { setEditingItem(null); setFormData({name:'', type:'follow_up', content:'', linked_form_id: ''}); setIsModalOpen(true); }} className="btn bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 py-2 text-sm flex items-center gap-2 shadow-sm"><Plus className="w-4 h-4" /> Trame Texte</button>
                                        <button onClick={() => { setEditingForm(null); setFormBuilderData({ name:'', description:'', fields:[], is_internal: true }); setIsFormBuilderOpen(true); }} className="btn btn-primary py-2 text-sm flex items-center gap-2"><Plus className="w-4 h-4" /> Masque (Cases)</button>
                                    </>
                                ) : (
                                    <button onClick={() => { 
                                        if(activeTab === 'files') setIsUploadModalOpen(true); 
                                        else if(activeTab === 'forms') { setEditingForm(null); setFormBuilderData({ name:'', description:'', fields:[], is_internal: false }); setIsFormBuilderOpen(true); } 
                                        else { setEditingItem(null); setFormData({name:'', type: activeTab === 'campaigns' ? 'campaign_sms' : 'courrier', content:'', linked_form_id: ''}); setIsModalOpen(true); } 
                                    }} className="btn btn-primary py-2 text-sm flex items-center gap-2"><Plus className="w-4 h-4" /> Créer</button>
                                )}
                            </div>
                        </div>
                        {loading ? <div className="text-center p-12"><div className="spinner"/></div> : (
                            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                                {displayItems.length === 0 && <p className="text-gray-400 col-span-full text-center py-10">Aucun élément trouvé.</p>}
                                {displayItems.map(item => (
                                    <div key={`${item._type}_${item.id}`} className="border border-gray-200 rounded-xl p-4 hover:shadow-md transition-all group bg-gray-50/50 flex flex-col">
                                        <div className="flex justify-between items-start mb-2">
                                            <div className="flex items-center gap-2">
                                                {item._type === 'form' ? <ClipboardList className="w-4 h-4 text-orange-500"/> : activeTab === 'files' && (['jpg','png','jpeg'].includes(item.file_type) ? <ImageIcon className="w-4 h-4 text-purple-500"/> : <FileText className="w-4 h-4 text-blue-500"/>)}
                                                {item._type !== 'form' && activeTab === 'campaigns' && <Megaphone className="w-4 h-4 text-orange-500"/>}
                                                {item._type !== 'form' && (activeTab === 'notes' || activeTab === 'documents') && <FileText className="w-4 h-4 text-blue-500"/>}
                                                <h3 className="font-bold text-gray-800 truncate pr-2">{item.name}</h3>
                                            </div>
                                            <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                {activeTab === 'files' ? <button onClick={() => handleDownload(item)} className="p-1.5 hover:bg-white rounded text-blue-600"><Download className="w-4 h-4"/></button> : item._type === 'form' ? <button onClick={() => { setEditingForm(item); setFormBuilderData({name:item.name, description:item.description||'', fields:JSON.parse(item.fields_json), is_internal: item.is_internal}); setIsFormBuilderOpen(true); }} className="p-1.5 hover:bg-white rounded text-blue-600"><Edit2 className="w-4 h-4"/></button> : <button onClick={() => { setEditingItem(item); setFormData({name:item.name, type:activeTab==='notes'?item.note_type:item.category, content:item.content, linked_form_id: item.linked_form_id || ''}); setIsModalOpen(true); }} className="p-1.5 hover:bg-white rounded text-blue-600"><Edit2 className="w-4 h-4"/></button>}
                                                <button onClick={() => handleDeleteItem(item)} className="p-1.5 hover:bg-white rounded text-red-600"><Trash2 className="w-4 h-4"/></button>
                                            </div>
                                        </div>
                                        
                                        {/* BADGE CATEGORIE */}
                                        {activeTab !== 'forms' && activeTab !== 'files' && (
                                            <span className={`text-[10px] border px-2 py-1 rounded uppercase font-bold w-fit mb-2 ${item._type === 'form' ? 'bg-orange-50 text-orange-600 border-orange-200' : 'bg-white border-gray-200 text-gray-500'}`}>
                                                {item._type === 'form' ? 'Masque de saisie' : (activeTab === 'notes' ? item.note_type : (item.category || '').replace('campaign_', ''))}
                                            </span>
                                        )}
                                        
                                        {/* BADGE CADENAS POUR LES FICHIERS */}
                                        {activeTab === 'files' && (
                                            <button 
                                                onClick={() => handleTogglePublic(item.id)}
                                                className={`text-[10px] px-2 py-1 rounded uppercase font-bold w-fit mb-2 flex items-center gap-1 transition-colors ${item.is_public ? 'bg-green-50 text-green-600 border border-green-200 hover:bg-green-100' : 'bg-gray-50 text-gray-500 border border-gray-200 hover:bg-gray-100'}`}
                                            >
                                                {item.is_public ? <><Unlock className="w-3 h-3"/> Public (Partageable)</> : <><Lock className="w-3 h-3"/> Privé (Sécurisé)</>}
                                            </button>
                                        )}

                                        {/* GESTION DU CONTENU OU DE LA DESCRIPTION */}
                                        {item._type === 'form' ? (
                                                <div className="mt-auto text-xs text-gray-500 bg-white p-2 rounded border border-gray-100 italic">{item.description || 'Aucune description'}</div>
                                            ) : (
                                                <div className="mt-auto text-xs text-gray-500 line-clamp-3 bg-white p-2 rounded border border-gray-100 font-mono">{(item.content || '').replace(/<[^>]*>?/gm, '')}</div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </>
                )}

                {/* --- VUE BIOLOGIE --- */}
                {activeTab === 'biology' && (
                    <div className="space-y-6 animate-in fade-in">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="font-bold text-gray-700">Groupes d'analyses biologiques</h2>
                            <div className="flex gap-2">
                                <button onClick={addBioGroup} className="btn bg-blue-50 text-blue-700 hover:bg-blue-100 flex items-center gap-2 py-2 text-sm"><Plus className="w-4 h-4"/> Groupe</button>
                                <button onClick={handleSaveBiology} className="btn btn-primary py-2 text-sm flex items-center gap-2"><Save className="w-4 h-4"/> Enregistrer</button>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                            {biologyGroups.map((g, idx) => (
                                <div key={idx} className="bg-blue-50/30 border border-blue-100 rounded-xl p-4 relative group">
                                    <div className="absolute right-2 top-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <button onClick={() => moveBioGroup(idx, 'up')} className="p-1 hover:bg-white rounded"><ArrowUp className="w-4 h-4 text-gray-500"/></button>
                                        <button onClick={() => moveBioGroup(idx, 'down')} className="p-1 hover:bg-white rounded"><ArrowDown className="w-4 h-4 text-gray-500"/></button>
                                        <button onClick={() => removeBioGroup(idx)} className="p-1 hover:bg-red-50 rounded"><Trash2 className="w-4 h-4 text-red-500"/></button>
                                    </div>
                                    <label className="text-[10px] font-bold text-blue-800 uppercase mb-1 block">Titre du groupe</label>
                                    <input value={g.title} onChange={e => updateBioGroupTitle(idx, e.target.value)} className="w-full text-sm font-bold border-blue-200 focus:ring-blue-300 mb-3" />
                                    <label className="text-[10px] font-bold text-gray-500 uppercase mb-1 block">Analyses (séparées par une virgule)</label>
                                    <textarea rows={3} value={g.raw_items !== undefined ? g.raw_items : g.items.join(', ')} onChange={e => updateBioGroupItems(idx, e.target.value)} className="w-full text-sm border-gray-200 focus:ring-sage-300 bg-white" placeholder="Ex: Glycémie à jeun, HbA1c..." />
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* --- MODALE TEXTE (Notes/Docs/Campagnes) --- */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
                    <div className={`bg-white rounded-2xl w-full shadow-2xl flex flex-col ${activeTab === 'documents' || (activeTab === 'campaigns' && formData.type === 'campaign_email') ? 'h-[80vh] max-w-4xl' : 'max-w-xl'}`}>
                        <div className="p-4 border-b border-gray-100 flex justify-between items-center bg-gray-50 rounded-t-2xl">
                            <h3 className="font-bold text-lg text-gray-800">{editingItem ? 'Modifier' : 'Nouveau'}</h3>
                            <button onClick={() => setIsModalOpen(false)}><X className="w-5 h-5 text-gray-400 hover:text-gray-600"/></button>
                        </div>
                        <div className="p-6 space-y-4 flex-1 overflow-y-auto">
                            <div className="grid grid-cols-2 gap-4">
                                <div><label className="text-xs font-bold text-gray-500 uppercase">Nom</label><input value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} className="w-full mt-1" /></div>
                                <div>
                                    <label className="text-xs font-bold text-gray-500 uppercase">Type</label>
                                    <select value={formData.type} onChange={e => setFormData({...formData, type: e.target.value})} className="w-full mt-1 bg-gray-50">
                                        {activeTab === 'notes' ? (
                                            <><option value="anamnese">Bilan Initial</option><option value="follow_up">Suivi</option><option value="free">Note Libre</option></>
                                        ) : activeTab === 'campaigns' ? (
                                            <><option value="campaign_sms">Modèle SMS</option><option value="campaign_email">Modèle Email</option></>
                                        ) : (
                                            <><option value="courrier">Courrier</option><option value="certificat">Certificat</option><option value="autre">Autre</option></>
                                        )}
                                    </select>
                                </div>
                            </div>
                            
                            {/* LIAISON QUESTIONNAIRE (Uniquement pour les Documents) */}
                            {activeTab === 'documents' && (
                                <div className="bg-orange-50 border border-orange-100 p-3 rounded-xl mt-2">
                                    <label className="text-[10px] font-bold text-orange-800 uppercase block mb-1">Lier à un masque de saisie / Questionnaire</label>
                                    <select 
                                        value={formData.linked_form_id} 
                                        onChange={e => setFormData({...formData, linked_form_id: e.target.value})}
                                        className="w-full text-sm bg-white border-orange-200"
                                    >
                                        <option value="">Aucun masque lié</option>
                                        {formTemplates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                                    </select>
                                </div>
                            )}

                            {/* BARRE D'OUTILS CHAMPS DYNAMIQUES */}
                            <div className="bg-blue-50 border border-blue-100 p-3 rounded-lg flex flex-col sm:flex-row gap-4 items-start sm:items-center">
                                <div className="flex-1">
                                    <span className="text-[10px] font-bold text-blue-800 uppercase block mb-1">Insérer une variable au curseur</span>
                                    <div className="flex flex-wrap gap-2">
                                        {DYNAMIC_VARS.map(v => (
                                            <button 
                                                key={v.val} type="button" 
                                                onClick={() => insertVariable(v.val)}
                                                className="text-xs bg-white text-blue-700 px-2 py-1 rounded border border-blue-200 hover:bg-blue-100 transition-colors shadow-sm"
                                            >
                                                {v.label}
                                            </button>
                                        ))}
                                    </div>
                                        {/* VARIABLES ISSUES DU QUESTIONNAIRE */}
                                        {activeTab === 'documents' && formData.linked_form_id && (() => {
                                            const linkedForm = formTemplates.find(f => f.id === parseInt(formData.linked_form_id));
                                            if (!linkedForm) return null;
                                            let fields = [];
                                            try { fields = JSON.parse(linkedForm.fields_json || '[]'); } catch(e){}
                                            
                                            return fields.map(f => {
                                                const magicTag = `{{${sanitizeFieldName(f.label)}}}`;
                                                return (
                                                    <button 
                                                        key={f.id} type="button" 
                                                        onClick={() => insertVariable(magicTag)}
                                                        className="text-xs bg-orange-100 text-orange-800 px-2 py-1 rounded border border-orange-200 hover:bg-orange-200 transition-colors font-mono shadow-sm"
                                                    >
                                                        + {magicTag}
                                                    </button>
                                                );
                                            });
                                        })()}
                                    </div>


                                {/*  INSERER UN FICHIER STATIQUE */}
                                {activeTab === 'campaigns' && (
                                    <div className="w-full sm:w-auto border-t sm:border-t-0 sm:border-l border-blue-200 pt-3 sm:pt-0 sm:pl-4">
                                        <span className="text-[10px] font-bold text-blue-800 uppercase block mb-1">Lien de fichier statique (En dur)</span>
                                        {libraryFiles.length > 0 ? (
                                            <select 
                                                className="text-xs bg-white text-blue-700 border-blue-200 w-full rounded py-1.5"
                                                onChange={(e) => {
                                                    if(e.target.value) {
                                                        const file = libraryFiles.find(f => f.id === parseInt(e.target.value));
                                                        const baseUrl = window.location.origin;
                                                        const fileLink = `${baseUrl}/api/library/files/p/${file.public_token}`;
                                                        const insertText = formData.type === 'campaign_email' 
                                                            ? `<a href="${fileLink}">Télécharger le fichier</a>` 
                                                            : ` Lien : ${fileLink} `;
                                                        setFormData(prev => ({...prev, content: prev.content + insertText}));
                                                        e.target.value = ""; // reset
                                                    }
                                                }}
                                            >
                                                <option value="">Sélectionnez un fichier public existant...</option>
                                                {libraryFiles.filter(f => f.is_public).map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                                            </select>
                                        ) : (
                                            <span className="text-[10px] text-gray-500 italic">Aucun fichier dans l'onglet 'Fichiers'</span>
                                        )}
                                    </div>
                                )}
                            </div>

                            <div className="flex-1 flex flex-col mt-4">
                                <label className="text-xs font-bold text-gray-500 uppercase mb-1">Contenu {formData.type === 'campaign_sms' && '(SMS = Texte sans mise en forme)'}</label>
                                {activeTab === 'notes' || formData.type === 'campaign_sms' ? (
                                    <textarea 
                                        ref={textAreaRef}
                                        value={formData.content} 
                                        onChange={e => setFormData({...formData, content: e.target.value})} 
                                        className="w-full flex-1 min-h-[300px] font-sans text-sm p-4 bg-gray-50 border-gray-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-sage-200"
                                    />
                                ) : (
                                    <ReactQuill 
                                        ref={quillRef}
                                        theme="snow" 
                                        value={formData.content} 
                                        onChange={(val) => setFormData({...formData, content: val})} 
                                        modules={quillModules} 
                                        className="h-[300px]"
                                    />
                                )}
                            </div>
                        </div>
                        <div className="p-4 border-t border-gray-100 flex justify-end gap-2 bg-gray-50 rounded-b-2xl">
                            <button onClick={() => setIsModalOpen(false)} className="btn bg-white border border-gray-300 text-gray-600">Annuler</button>
                            <button onClick={handleSaveText} className="btn btn-primary flex items-center gap-2"><Save className="w-4 h-4"/> Enregistrer</button>
                        </div>
                    </div>
                </div>
            )}

            {/* MODALES UPLOAD & FORMBUILDER (Restent inchangées) */}
            {isUploadModalOpen && (
                <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl p-6">
                    <h3 className="text-xl font-bold text-gray-800 mb-4">Importer un fichier</h3>
                    <div className="space-y-4">
                        <div>
                        <label className="text-xs font-bold text-gray-500 uppercase">Nom</label>
                        <input
                            value={uploadData.name}
                            onChange={e => setUploadData({...uploadData, name: e.target.value})}
                            className="w-full mt-1"
                        />
                        </div>
                        <div>
                        <label className="text-xs font-bold text-gray-500 uppercase">Catégorie</label>
                        <select
                            value={uploadData.category}
                            onChange={e => setUploadData({...uploadData, category: e.target.value})}
                            className="w-full mt-1"
                        >
                            <option value="guide">Guide</option>
                            <option value="recette">Recette</option>
                            <option value="autre">Autre</option>
                        </select>
                        </div>
                        <div>
                        <input
                            type="file"
                            onChange={e => setUploadData({...uploadData, file: e.target.files[0]})}
                            className="w-full mt-1 border border-gray-200 rounded p-2 text-sm"
                        />
                        </div>
                        <label className="flex items-center gap-2 cursor-pointer mt-2">
                        <input
                            type="checkbox"
                            checked={uploadData.is_public}
                            onChange={e => setUploadData({...uploadData, is_public: e.target.checked})}
                            className="w-4 h-4 rounded text-sage-600 focus:ring-sage-500 border-gray-300"
                        />
                        <span className="text-sm font-bold text-gray-700">
                            Fichier Public (Peut être partagé aux patients via un lien sécurisé)
                        </span>
                        </label>
                        <button
                        onClick={handleUploadFile}
                        className="w-full btn btn-primary mt-4"
                        >
                        Importer
                        </button>
                        <button
                        onClick={() => setIsUploadModalOpen(false)}
                        className="w-full text-gray-500 text-sm hover:underline"
                        >
                        Annuler
                        </button>
                    </div>
                    </div>
                </div>
                )}
            
            {/* MODALE FORM BUILDER */}
            {isFormBuilderOpen && (
                <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
                    <div className="bg-white rounded-2xl w-full max-w-4xl shadow-2xl flex flex-col h-[90vh]">
                        {/* Header Modale */}
                        <div className="p-4 border-b border-gray-100 flex justify-between items-center bg-gray-50 rounded-t-2xl">
                            <h3 className="font-serif font-bold text-xl text-gray-800 flex items-center gap-2">
                                <ClipboardList className="w-5 h-5 text-orange-500"/> 
                                {editingForm 
                                    ? (formBuilderData.is_internal ? 'Modifier le masque' : 'Modifier le questionnaire') 
                                    : (formBuilderData.is_internal ? 'Nouveau Masque de Saisie (Interne)' : 'Nouveau Questionnaire Patient')}
                            </h3>
                            <button onClick={() => setIsFormBuilderOpen(false)}><X className="w-6 h-6 text-gray-400 hover:text-gray-600" /></button>
                        </div>
                        
                        {/* Corps Scrollable */}
                        <div className="flex-1 overflow-y-auto p-6 bg-gray-50/30">
                            {/* Infos générales */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8 bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
                                <div>
                                    <label className="text-xs font-bold text-gray-500 uppercase mb-1 block">Titre du questionnaire</label>
                                    <input 
                                        value={formBuilderData.name} 
                                        onChange={e => setFormBuilderData({...formBuilderData, name: e.target.value})} 
                                        placeholder="Ex: Bilan de Suivi Mensuel" 
                                        className="w-full font-bold text-gray-800" 
                                    />
                                </div>
                                <div>
                                    <label className="text-xs font-bold text-gray-500 uppercase mb-1 block">Petit texte d'introduction (optionnel)</label>
                                    <input 
                                        value={formBuilderData.description} 
                                        onChange={e => setFormBuilderData({...formBuilderData, description: e.target.value})} 
                                        placeholder="Ex: Merci de remplir ce formulaire avant notre séance..." 
                                        className="w-full text-sm" 
                                    />
                                </div>
                            </div>

                            {/* Les Questions */}
                            <div className="space-y-4">
                                {formBuilderData.fields.map((field, index) => (
                                    <div key={field.id} className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm relative group">
                                        
                                        {/* Actions à droite (Up/Down/Delete) */}
                                        <div className="absolute right-4 top-4 flex gap-1 opacity-50 group-hover:opacity-100 transition-opacity">
                                            <button type="button" onClick={() => handleMoveFormField(index, 'up')} disabled={index === 0} className="p-1 hover:bg-gray-100 rounded disabled:opacity-30"><ArrowUp className="w-4 h-4"/></button>
                                            <button type="button" onClick={() => handleMoveFormField(index, 'down')} disabled={index === formBuilderData.fields.length - 1} className="p-1 hover:bg-gray-100 rounded disabled:opacity-30"><ArrowDown className="w-4 h-4"/></button>
                                            <div className="w-px h-4 bg-gray-300 mx-1 mt-1"></div>
                                            <button type="button" onClick={() => handleRemoveFormField(field.id)} className="p-1 hover:bg-red-50 text-red-500 rounded"><Trash2 className="w-4 h-4"/></button>
                                        </div>

                                        <div className="grid grid-cols-12 gap-4 pr-24">
                                            {/* Question Label */}
                                            <div className="col-span-12 md:col-span-7">
                                                <label className="text-[10px] font-bold text-gray-400 uppercase">La Question</label>
                                                <input 
                                                    value={field.label} 
                                                    onChange={e => handleUpdateFormField(field.id, 'label', e.target.value)} 
                                                    placeholder="Quelle est votre question ?" 
                                                    className="w-full text-sm font-medium" 
                                                />
                                            </div>
                                            
                                            {/* Type */}
                                            <div className="col-span-6 md:col-span-3">
                                                <label className="text-[10px] font-bold text-gray-400 uppercase">Type de réponse</label>
                                                <select 
                                                    value={field.type} 
                                                    onChange={e => handleUpdateFormField(field.id, 'type', e.target.value)} 
                                                    className="w-full text-sm bg-gray-50"
                                                >
                                                    <option value="text">Texte Court</option>
                                                    <option value="textarea">Texte Long (Paragraphe)</option>
                                                    <option value="richtext">Texte Formaté (Éditeur Riche)</option>
                                                    <option value="repeater">Liste dynamique (Bouton +)</option>
                                                    <option disabled>──────</option>
                                                    <option value="number">Nombre</option>
                                                    <option value="radio">Choix unique (Boutons)</option>
                                                    <option value="checkbox">Choix multiples (Cases)</option>
                                                    <option value="sys_weight">Système: Poids actuel (Kg)</option>
                                                    <option value="sys_height">Système: Taille (cm)</option>
                                                </select>

                                            </div>

                                            {/* Required */}
                                            <div className="col-span-6 md:col-span-2 flex flex-col justify-end pb-2">
                                                <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                                                    <input 
                                                        type="checkbox" 
                                                        checked={field.required} 
                                                        onChange={e => handleUpdateFormField(field.id, 'required', e.target.checked)} 
                                                        className="w-4 h-4 text-sage-600 rounded focus:ring-sage-500"
                                                    />
                                                    Obligatoire
                                                </label>
                                            </div>
                                        </div>

                                        {/* Options pour Radio/Checkbox */}
                                        {(field.type === 'radio' || field.type === 'checkbox') && (
                                            <div className="mt-3 bg-yellow-50 p-3 rounded-lg border border-yellow-100">
                                                <label className="text-[10px] font-bold text-yellow-800 uppercase">Options (séparées par des virgules)</label>
                                                <input 
                                                    value={field.options} 
                                                    onChange={e => handleUpdateFormField(field.id, 'options', e.target.value)} 
                                                    placeholder="Ex: Excellent, Moyen, Difficile" 
                                                    className="w-full text-sm mt-1 border-yellow-200 focus:border-yellow-400 focus:ring-yellow-100" 
                                                />
                                            </div>
                                        )}
                                        
                                        {/* Aide pour champs système */}
                                        {(field.type.startsWith('sys_')) && (
                                            <div className="mt-3 text-xs text-blue-600 bg-blue-50 px-3 py-1.5 rounded-lg border border-blue-100">
                                                💡 Ce champ mettra automatiquement à jour la courbe de {field.type === 'sys_weight' ? 'poids' : 'taille'} du patient sur sa fiche.
                                            </div>
                                        )}
                                    </div>
                                ))}

                                <button 
                                    type="button" 
                                    onClick={handleAddFormField} 
                                    className="w-full py-4 border-2 border-dashed border-gray-300 rounded-xl text-gray-500 font-bold hover:bg-gray-50 hover:border-sage-400 hover:text-sage-600 transition-all flex justify-center items-center gap-2"
                                >
                                    <Plus className="w-5 h-5"/> Ajouter une question
                                </button>
                            </div>
                        </div>

                        {/* Footer Modale */}
                        <div className="p-4 border-t border-gray-100 bg-white rounded-b-2xl flex justify-end gap-3 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)]">
                            <button onClick={() => setIsFormBuilderOpen(false)} className="btn bg-gray-100 text-gray-600 hover:bg-gray-200 px-6">Annuler</button>
                            <button onClick={handleSaveFormTemplate} className="btn btn-primary px-8 flex items-center gap-2"><Save className="w-4 h-4"/> Enregistrer le formulaire</button>
                        </div>
                    </div>
                </div>
            )}

        </div>
    )
}